import { onMounted, onUnmounted } from 'vue'
import { useBackendStore } from '@/stores/system'

/**
 * Keeps `backendStore.isConnected` in sync with the browser's connectivity, so
 * ShowError.vue can tell "no internet" apart from "the API answered badly".
 *
 * Before AAR-1670 nothing ever called `connectionSet()`, so `isConnected` was
 * permanently true and the "Keine Internetverbindung" branch of ShowError.vue was
 * unreachable — a dropped connection surfaced as the generic "Es ist ein Fehler
 * aufgetreten" instead, and only once a request had failed.
 *
 * Deliberately narrow: this does NOT touch `userStore.isOnline`. That flag gates
 * the experimental IndexedDB persister in useTable, which is intentionally not
 * enabled yet (see src/plugins/queryClient) — wiring it here would switch
 * production onto that path as a side effect. `useLoadOfflineComponents` keeps
 * owning `isOnline` for the dev-only offline mode.
 */
export function useConnectionStatus() {
	const { connectionSet } = useBackendStore()

	function handleOnline() {
		connectionSet(true)
	}

	function handleOffline() {
		connectionSet(false)
	}

	onMounted(() => {
		// Seed from the current value: the app can boot while already offline, in
		// which case no event ever fires.
		connectionSet(navigator.onLine)
		window.addEventListener('online', handleOnline)
		window.addEventListener('offline', handleOffline)
	})

	onUnmounted(() => {
		window.removeEventListener('online', handleOnline)
		window.removeEventListener('offline', handleOffline)
	})
}
